import React, { useEffect, useState, useCallback } from "react";
import { ArrowUpRight, ShieldCheck, AlertTriangle, ArrowDownRight, Sparkles, X } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import api, { apiError, redirectToCheckout } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLANS = [
  { key: "free", name: "Free", price: 0, limit: "1 active project" },
  { key: "growth", name: "Growth", price: 29, limit: "Up to 5 active projects" },
  { key: "scale", name: "Scale", price: 79, limit: "Unlimited active projects" },
];
const ORDER = { free: 0, growth: 1, scale: 2 };

const CANCEL_REASONS = [
  "Too expensive",
  "Not using it enough",
  "Missing a feature I need",
  "Found a better alternative",
  "Only needed it temporarily / project finished",
  "Technical issues / bugs",
  "Other",
];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };

const Account = () => {
  const { refreshMe } = useAuth();
  const [sub, setSub] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelStep, setCancelStep] = useState("retain");
  const [busy, setBusy] = useState(false);
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/billing/subscription");
    setSub(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upgrade = async (key, interval = "monthly") => {
    try {
      const { data } = await api.post("/billing/checkout", { plan: key, interval, origin_url: window.location.origin });
      redirectToCheckout(data.url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openCancel = () => { setCancelStep("retain"); setReason(""); setReasonNote(""); setCancelOpen(true); };

  const [annualOpen, setAnnualOpen] = useState(false);
  const [annualSuccess, setAnnualSuccess] = useState(null);
  const celebrateAnnual = () => {
    const end = Date.now() + 900;
    const colors = ["#10b981", "#34d399", "#2563eb", "#a7f3d0"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };
  const savingText = (s) => (Number.isInteger(s) ? `£${s}` : `£${s.toFixed(2)}`);
  const switchToAnnual = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/switch-to-annual", { origin_url: window.location.origin });
      setAnnualOpen(false);
      setAnnualSuccess({ planName: data.plan_name, saving: data.annual_saving });
      celebrateAnnual();
      await load();
      await refreshMe();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDowngrade = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/downgrade", { plan: key, origin_url: window.location.origin });
      toast.success(`You'll switch to ${key === "growth" ? "Growth" : key} on ${fmtDate(data.effective_date)}`);
      setCancelOpen(false);
      await load(); refreshMe();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmCancel = async () => {
    if (!reason) { toast.error("Please choose a reason for cancelling"); return; }
    setBusy(true);
    try {
      await api.post("/billing/cancel", { origin_url: window.location.origin, reason, reason_note: reasonNote });
      toast.success("Subscription cancelled. We've emailed you the details.");
      setCancelOpen(false);
      await load(); refreshMe();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resume = async () => {
    try {
      await api.post("/billing/resume");
      toast.success("Subscription reinstated — your data is safe.");
      load(); refreshMe();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const current = sub?.plan || "free";
  const canDowngradeToGrowth = current === "scale";

  return (
    <AppShell>
      <div className="border-b border-slate-200 bg-white px-6 md:px-8 py-5">
        <h1 className="font-display text-2xl font-bold text-slate-900">Billing &amp; Plan</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your subscription and active-project capacity.</p>
      </div>

      <div className="p-6 md:p-8 max-w-4xl">
        {/* Annual switch success — persistent until dismissed */}
        {annualSuccess && (
          <div className="mb-6 rounded-xl border border-emerald-300 bg-emerald-50 p-5 flex items-start gap-3" data-testid="annual-success-banner">
            <Sparkles className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-display font-semibold text-emerald-900 text-sm" data-testid="annual-success-heading">You're now on {annualSuccess.planName} 🎉</p>
              <p className="text-sm text-emerald-800 mt-1" data-testid="annual-success-saving">That's 2 months free — you save <strong>{savingText(annualSuccess.saving)} a year</strong>. We've emailed your confirmation.</p>
            </div>
            <button onClick={() => setAnnualSuccess(null)} data-testid="annual-success-dismiss" className="text-emerald-600 hover:text-emerald-800 shrink-0" aria-label="Dismiss">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Scheduled deletion warning */}
        {sub?.data_deletion_at && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="deletion-warning">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-display font-semibold text-amber-900 text-sm">Your subscription is cancelled</p>
              <p className="text-sm text-amber-800 mt-1">
                Please export anything you want to keep. Any projects, takeoffs, estimates and billing not exported will be <strong>permanently deleted on {fmtDate(sub.data_deletion_at)}</strong>. Reinstate any time before then to cancel the deletion.
              </p>
              <Button size="sm" onClick={resume} data-testid="reinstate-sub-btn" className="mt-3 bg-blue-600 hover:bg-blue-700">Reinstate subscription</Button>
            </div>
          </div>
        )}

        {/* Scheduled downgrade banner */}
        {sub?.scheduled_plan && (
          <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-5 flex items-start gap-3" data-testid="downgrade-banner">
            <ArrowDownRight className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-display font-semibold text-blue-900 text-sm">Downgrade scheduled</p>
              <p className="text-sm text-blue-800 mt-1">Your plan switches to <strong>{sub.scheduled_plan_name}</strong> on {fmtDate(sub.scheduled_plan_date)}. You keep your current plan until then.</p>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="current-plan-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Current plan</p>
              <p className="font-display text-2xl font-bold text-navy mt-1">{sub?.plan_name || "Free"}</p>
              <p className="text-sm text-slate-500 mt-1" data-testid="account-usage">
                {sub ? `${sub.active_projects} active project${sub.active_projects === 1 ? "" : "s"}${sub.active_limit != null ? ` of ${sub.active_limit}` : " · unlimited"}` : "—"}
              </p>
            </div>
            <div className="text-right">
              {sub?.subscription_status === "active" && (
                <span className="inline-flex items-center gap-1.5 text-sm text-green-700 bg-green-50 px-3 py-1 rounded-full"><ShieldCheck className="h-4 w-4" /> Active</span>
              )}
              {current !== "free" && (
                <p className="text-xs text-slate-400 mt-2" data-testid="account-billing-interval">{sub?.billing_interval === "annual" ? "Billed annually (2 months free)" : "Billed monthly (every 30 days)"}</p>
              )}
              {current !== "free" && !sub?.cancel_at_period_end && sub?.current_period_end && (
                <p className="text-xs text-slate-500 mt-1" data-testid="renewal-date">Renews on {fmtDate(sub.current_period_end)}</p>
              )}
              {sub?.cancel_at_period_end && (
                <p className="text-xs text-amber-600 mt-1">Cancels on {fmtDate(sub.current_period_end)}</p>
              )}
            </div>
          </div>
          {current !== "free" && !sub?.cancel_at_period_end && (
            <Button variant="outline" onClick={openCancel} data-testid="cancel-sub-btn" className="mt-5 border-slate-300 text-slate-600">Cancel subscription</Button>
          )}
          {current !== "free" && sub?.cancel_at_period_end && (
            <Button variant="outline" onClick={resume} data-testid="resume-sub-btn" className="mt-5 border-blue-300 text-blue-700">Reinstate subscription</Button>
          )}
        </div>

        {sub?.trial_active && (
          <div className="mt-4 rounded-xl p-5 border border-blue-200 bg-blue-50 flex items-start gap-3" data-testid="trial-banner">
            <Sparkles className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-blue-900">You're on a free {sub.trial_plan_name} trial</p>
              <p className="text-sm text-blue-800/80 mt-0.5">Full {sub.trial_plan_name} access until <strong>{fmtDate(sub.trial_ends_at)}</strong> — no card needed. Add a paid plan any time to keep your projects running after that.</p>
            </div>
          </div>
        )}

        {/* Annual nudge — only for committed monthly customers (2+ paid invoices) */}
        {current !== "free" && sub?.billing_interval !== "annual" && !sub?.cancel_at_period_end && (sub?.paid_invoice_count || 0) >= 2 && (() => {
          const m = (PLANS.find((p) => p.key === current) || {}).price || 0;
          return (
            <div className="mt-4 rounded-xl p-5 border border-emerald-200 bg-emerald-50 flex items-start justify-between gap-4 flex-wrap" data-testid="annual-nudge">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-900">Save 2 months — pay annually</p>
                  <p className="text-sm text-emerald-800/80 mt-0.5">Switch to annual and get 2 months free: <strong>£{m * 10}/yr</strong> instead of £{m * 12} — you save <strong>£{m * 2}</strong> a year. We'll credit the unused part of this month.</p>
                </div>
              </div>
              <Button onClick={() => setAnnualOpen(true)} data-testid="switch-annual-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">Switch to annual</Button>
            </div>
          );
        })()}

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="font-display text-lg font-bold text-slate-900">Change plan</h2>
          {current === "free" && (
            <div className="inline-flex self-start rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="plan-interval-toggle">
              <button
                onClick={() => setBillingInterval("monthly")}
                data-testid="interval-monthly"
                className={`px-4 py-1.5 text-sm rounded-md transition-colors ${billingInterval === "monthly" ? "bg-white shadow-sm text-slate-900 font-semibold" : "text-slate-500 hover:text-slate-700"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval("annual")}
                data-testid="interval-annual"
                className={`px-4 py-1.5 text-sm rounded-md transition-colors ${billingInterval === "annual" ? "bg-white shadow-sm text-slate-900 font-semibold" : "text-slate-500 hover:text-slate-700"}`}
              >
                Annual <span className="text-emerald-600 font-semibold">· 2 months free</span>
              </button>
            </div>
          )}
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          {PLANS.map((p) => {
            const isCurrent = p.key === current;
            const isDowngrade = ORDER[p.key] < ORDER[current];
            const annual = current === "free" && billingInterval === "annual" && p.key !== "free";
            const shownPrice = annual ? p.price * 10 : p.price;
            const unit = annual ? "/yr" : "/mo";
            return (
              <div key={p.key} className={`bg-white rounded-xl p-6 border ${p.key === "growth" ? "border-blue-300" : "border-slate-200"}`} data-testid={`account-plan-${p.key}`}>
                <p className="font-display font-bold text-navy">{p.name}</p>
                <p className="font-mono text-2xl font-bold text-slate-900 mt-2" data-testid={`account-plan-price-${p.key}`}>£{shownPrice}<span className="text-sm text-slate-400 font-sans">{unit}</span></p>
                <p className="text-sm text-slate-500 mt-1">{p.limit}</p>
                {p.key !== "free" && <p className="text-xs text-slate-400 mt-0.5">{annual ? "2 months free · cancel anytime" : "Recurring · cancel anytime"}</p>}
                {/* Growth is a real paid downgrade target for Scale users */}
                {isDowngrade && p.key === "growth" && current === "scale" ? (
                  <Button
                    variant="outline"
                    disabled={busy || !!sub?.scheduled_plan}
                    onClick={() => doDowngrade("growth")}
                    data-testid="account-downgrade-growth"
                    className="mt-4 w-full border-slate-300 text-slate-700"
                  >
                    {sub?.scheduled_plan === "growth" ? "Scheduled" : "Downgrade to Growth"}
                    {!sub?.scheduled_plan && <ArrowDownRight className="h-4 w-4 ml-1" />}
                  </Button>
                ) : (
                  <Button
                    disabled={isCurrent || isDowngrade || p.key === "free"}
                    onClick={() => (p.key === "free" ? openCancel() : upgrade(p.key, current === "free" ? billingInterval : "monthly"))}
                    data-testid={`account-upgrade-${p.key}`}
                    className={`mt-4 w-full ${p.key === "growth" ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}
                  >
                    {isCurrent ? "Current" : isDowngrade ? "Downgrade" : p.key === "free" ? "Free" : `Upgrade to ${p.name}`}
                    {!isCurrent && !isDowngrade && p.key !== "free" && <ArrowUpRight className="h-4 w-4 ml-1" />}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-4">All features are available on every plan — only active project capacity changes. Paid downgrades take effect at the end of your current billing period.</p>
      </div>

      {/* Switch to annual confirmation */}
      <Dialog open={annualOpen} onOpenChange={setAnnualOpen}>
        <DialogContent className="sm:max-w-md" data-testid="switch-annual-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Switch to annual billing</DialogTitle>
            <DialogDescription>
              {(() => { const m = (PLANS.find((p) => p.key === current) || {}).price || 0;
                return `You'll be charged the annual price of £${m * 10} today (2 months free), less a pro-rated credit for the unused part of your current month. Your plan will then renew once a year.`; })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnualOpen(false)} disabled={busy}>Not now</Button>
            <Button onClick={switchToAnnual} disabled={busy} data-testid="confirm-switch-annual-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">{busy ? "Switching…" : "Confirm & switch"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation flow */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="cancel-dialog">
          {cancelStep === "retain" ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Before you go…</DialogTitle>
                <DialogDescription>Let's find an option that works better for you.</DialogDescription>
              </DialogHeader>
              {sub?.billing_interval === "annual" && sub?.current_period_end && (() => {
                const end = new Date(sub.current_period_end);
                const days = Math.max(0, Math.ceil((end - Date.now()) / 86400000));
                const months = Math.round(days / 30);
                const timeLeft = months >= 1 ? `about ${months} month${months === 1 ? "" : "s"}` : `${days} day${days === 1 ? "" : "s"}`;
                return (
                  <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="annual-prepaid-guard">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-display font-semibold text-amber-900">You've already prepaid until {fmtDate(sub.current_period_end)}</p>
                        <p className="text-sm text-amber-800 mt-1">You're on annual billing with <strong>{timeLeft}</strong> left already paid for. If you cancel now you'll keep full access until then, but the remaining prepaid time <strong>isn't refunded</strong>. You may prefer to keep using it right up to your renewal date.</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {canDowngradeToGrowth ? (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-5" data-testid="retention-downgrade-offer">
                  <div className="flex items-start gap-3">
                    <span className="h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-blue-600" /></span>
                    <div>
                      <p className="font-display font-semibold text-blue-900">Switch to Growth for £29/mo instead</p>
                      <p className="text-sm text-blue-800 mt-1">Keep working on up to 5 active projects for less, rather than losing your paid workspace. The change takes effect at the end of your current billing period — no charge today.</p>
                    </div>
                  </div>
                  <Button onClick={() => doDowngrade("growth")} disabled={busy} data-testid="retain-downgrade-btn" className="mt-4 w-full bg-blue-600 hover:bg-blue-700">
                    Downgrade to Growth (£29/mo)
                  </Button>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="retention-value">
                  <p className="font-display font-semibold text-slate-900">You'll lose your paid capacity</p>
                  <p className="text-sm text-slate-600 mt-1">Cancelling drops you to the Free plan (1 active project) at the end of your billing period. You can keep everything running for just £29/mo — are you sure you want to cancel?</p>
                </div>
              )}
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="ghost" onClick={() => setCancelOpen(false)} data-testid="keep-plan-btn" className="text-slate-600">Keep my plan</Button>
                <Button variant="outline" onClick={() => setCancelStep("confirm")} data-testid="continue-cancel-btn" className="border-slate-300 text-slate-700">Continue to cancel</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-slate-900">Cancel your subscription?</DialogTitle>
                <DialogDescription>Please read this carefully before confirming.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-slate-200 p-3 text-slate-600">
                  Your subscription stays active until <strong>{fmtDate(sub?.current_period_end)}</strong>, then moves to the Free plan. You can <strong>reinstate at any time</strong>.
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">What's the main reason you're cancelling? <span className="text-red-500">*</span></label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="cancel-reason-select" className="w-full">
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {CANCEL_REASONS.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`cancel-reason-${r.split(" ")[0].toLowerCase()}`}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    value={reasonNote}
                    onChange={(e) => setReasonNote(e.target.value)}
                    data-testid="cancel-reason-note"
                    placeholder="Anything else you'd like us to know? (optional)"
                    className="mt-2 resize-none"
                    rows={2}
                    maxLength={1000}
                  />
                </div>
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="confirm-deletion-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <span className="text-amber-800">Anything you don't export within <strong>30 days of cancellation</strong> — projects, takeoffs, estimates and billing — will be <strong>permanently deleted</strong> and cannot be recovered. Reinstating before then aborts the deletion.</span>
                </div>
                <p className="text-xs text-slate-400">Cancellation is subject to our <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-600 underline">Terms of Service</a>.</p>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="ghost" onClick={() => setCancelStep("retain")} data-testid="cancel-back-btn" className="text-slate-600">Back</Button>
                <Button onClick={confirmCancel} disabled={busy || !reason} data-testid="confirm-cancel-btn" className="bg-red-600 hover:bg-red-700 text-white">
                  <X className="h-4 w-4 mr-1" /> Cancel my subscription
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default Account;
